import { tweenPromise, tweenWaitPromise } from '@Animation/utils'
import {
  MULTIPLIER_KENO_SNAKES_ATLAS_KEYS,
  MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS,
} from './asset-preloader'
import { MultiplierAnimation, MultiplierAnimationResult } from '../multiplier-animation'
import { Grid, GridLocation } from '@Animation/game-objects/grid'
import { KENO_SNAKES_AUDIO_KEYS, KenoSnake, KenoSnakesUtils } from '@Animation/mechanics/keno-snakes'
import { Utils } from '@Util/utils'
import { store } from '@AppState/redux-store'
import { AnimationActions } from '@AppState/animation'
import { SoundController } from '@Animation/sound-controller'

const CIRCLE_POSITIONS: Record<number, { xPositions: number[]; yPositions: number[] }> = {
  3: {
    xPositions: [-150, 0, 150],
    yPositions: [-150, 0, 150],
  },
  4: {
    xPositions: [-190, -65, 65, 190],
    yPositions: [-190, -65, 65, 190],
  },
}

const REMOVED_CIRCLES: Record<number, number[]> = {
  1: [0, 1, 2, 3, 5, 6, 7, 8],
  2: [0, 1, 2, 4, 6, 7, 8],
  3: [0, 1, 2, 6, 7, 8],
  4: [0, 2, 4, 6, 8],
  5: [0, 2, 6, 8],
  6: [1, 4, 7],
  7: [2, 6],
  8: [4],
  9: [],
  10: [0, 2, 7, 8, 13, 15],
  11: [0, 7, 8, 13, 15],
  12: [0, 7, 8, 15],
  13: [0, 8, 15],
  14: [0, 15],
  15: [0],
  16: [],
}

export class PossibleMultiplier extends Phaser.GameObjects.Container {
  private circle: Phaser.GameObjects.Image
  private outline: Phaser.GameObjects.Image
  private text: Phaser.GameObjects.Text
  private pulse: Phaser.GameObjects.Image

  private fontSize: number = 32

  location: GridLocation
  value: number

  static noBonusSound: Phaser.Sound.BaseSound | null = null
  static bonusSound: Phaser.Sound.BaseSound | null = null
  static bonusEliminatedSound: Phaser.Sound.BaseSound | null = null

  constructor(
    scene: Phaser.Scene, 
    x: number, 
    y: number, 
    location: GridLocation, 
    value: number,
    soundAssets: {
      noBonusSound: string,
      bonusSound: string,
      bonusEliminatedSound: string
    }
  ) {
    super(scene, x, y)

    this.value = value
    this.location = location

    if(PossibleMultiplier.noBonusSound === null){
      PossibleMultiplier.noBonusSound = scene.sound.add(soundAssets.noBonusSound)
    }

    if(PossibleMultiplier.bonusSound === null){
      PossibleMultiplier.bonusSound = scene.sound.add(soundAssets.bonusSound)
    }
    
    if(PossibleMultiplier.bonusEliminatedSound === null){
      PossibleMultiplier.bonusEliminatedSound = scene.sound.add(soundAssets.bonusEliminatedSound)
    }

    this.circle = this.scene.add.image(
      0,
      0,
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.CIRCLE_BLUE_SMALL,
      value % 5,
    )
    this.outline = this.scene.add.image(
      0,
      0,
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.CIRCLE_BLUE_OUTLINE_SMALL,
    )
    this.pulse = this.scene.add.image(
      0,
      5,
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.PULSE_SMALL_0,
    )
    this.pulse.setScale(1.15)
    this.pulse.setName('Pulse')

    this.circle.setOrigin(0.5, 0.5)
    this.outline.setOrigin(0.5, 0.5)
    this.pulse.setOrigin(0.5, 0.5)
    this.pulse.setVisible(false)

    let textStr = `${value}x`
    const hostConfig = store.getState().hostConfig.config
    if (
      value === 1 &&
      hostConfig !== null &&
      hostConfig.multiplierConfig &&
      hostConfig.multiplierConfig.noMultiplierDisplayString
    ) {
      textStr = hostConfig.multiplierConfig.noMultiplierDisplayString

      if (textStr.length > 3) {
        this.fontSize = 28
      }
    }

    this.text = this.scene.add.text(0, 0, textStr, {
      fontSize: this.fontSize,
      color: '#000000',
      align: 'center',
      fontFamily: 'Jost',
      wordWrap: { width: 105, useAdvancedWrap: true },
      lineSpacing: -this.fontSize / 3,
    })
    this.text.setOrigin(0.5, 0.5)

    tweenPromise(this.scene, {
      targets: this.outline,
      rotation: Phaser.Math.PI2,
      duration: 5000,
      repeat: -1,
    })()

    this.add([this.circle, this.outline, this.text, this.pulse])
    this.pulse.setToTop()
  }

  async disappear() {
    this.disappearPulse()

    if(PossibleMultiplier.bonusEliminatedSound != null){
      SoundController.playSound(PossibleMultiplier.bonusEliminatedSound)
    }
    
    await tweenPromise(this.scene, {
      targets: this,
      alpha: 0,
      duration: 250,
    })()
  }

  private async disappearPulse() {
    this.pulse.setVisible(true)
    this.pulse.alpha = 1

    await tweenWaitPromise(this.scene, 50)()

    this.pulse.setTexture(
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.PULSE_SMALL_1,
    )
    await tweenPromise(this.scene, {
      targets: this.pulse,
      alpha: 0,
      duration: 125,
    })()
  }

  private async winnerPulse() {
    this.pulse.setTexture(
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.PULSE_LARGE_0,
    )

    this.pulse.setVisible(true)
    this.pulse.alpha = 1

    await tweenWaitPromise(this.scene, 50)()

    this.pulse.setTexture(
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.PULSE_LARGE_1,
    )
    await tweenPromise(this.scene, {
      targets: this.pulse,
      alpha: 0,
      duration: 250,
    })()
  }

  async showAsWinner() {
    this.circle.setTexture(MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.CIRCLE_BLUE_LARGE, this.value % 5)
    this.outline.setTexture(
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.CIRCLE_YELLOW_OUTLINE_LARGE,
    )
    this.pulse.setTexture(
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.PULSE_LARGE_0,
    )

    this.circle.setScale(0.566)
    this.outline.setScale(0.566)
    this.pulse.setScale(0.566)

    const blueCircle = this.scene.add.image(
      0,
      0,
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.CIRCLE_YELLOW_LARGE,
    )
    blueCircle.setOrigin(0.5, 0.5)
    blueCircle.setScale(0.0849)
    this.add(blueCircle)
    this.moveBelow(blueCircle, this.outline)

    const originalFontSize = this.fontSize
    const fontIncreaseAmount = this.fontSize


    if(PossibleMultiplier.noBonusSound && this.value === 1){
      SoundController.playSound( PossibleMultiplier.noBonusSound )
    } else if (PossibleMultiplier.bonusSound) {
      SoundController.playSound( PossibleMultiplier.bonusSound )
    }


    tweenPromise(this.scene, {
      targets: this,
      x: 0,
      y: 0,
      duration: 600,
    })()

    const circleGrow = tweenPromise(this.scene, {
      targets: [this.circle, this.pulse, blueCircle],
      scale: 1.25,
      duration: 600,
      onUpdate: (tween) => {
        const t = tween.progress
        const fontSize = originalFontSize + fontIncreaseAmount * t

        this.text.setStyle({
          fontSize: fontSize,
          align: 'center',
          wordWrap: { width: 105 + 105 * t, useAdvancedWrap: true },
        })
      },
    })

    const outlineGrow = tweenPromise(this.scene, {
      targets: [this.outline],
      scale: 1.3,
      duration: 600,
      onUpdate: (tween) => {
        const t = tween.progress
        const fontSize = originalFontSize + fontIncreaseAmount * t

        this.text.setStyle({
          fontSize: fontSize,
          align: 'center',
          wordWrap: { width: 105 + 105 * t, useAdvancedWrap: true },
        })
      },
    })

    await Promise.all([circleGrow(), outlineGrow()])

    await this.winnerPulse()
  }
}

export class KenoSnakesMultiplierAnimation extends MultiplierAnimation {
  background: Phaser.GameObjects.Image
  foreground: Phaser.GameObjects.Image

  circleContainer: Phaser.GameObjects.Container

  grid: Grid
  possibleValues: number[]

  snake: KenoSnake
  allCircles: PossibleMultiplier[] = []
  remainingTargets: PossibleMultiplier[] = []
  resultCircle!: PossibleMultiplier

  snakeSpeedMultiplier: number = 1

  constructor(scene: Phaser.Scene, x: number, y: number, config: MultiplierAnimationResult) {
    super(scene, x, y, config)

    this.loadTextures()

    const shuffledValues = Utils.shuffleArray(config.possibleResults)

    this.possibleValues = shuffledValues
    const numPossibleValues = shuffledValues.length

    if (numPossibleValues > 16 || numPossibleValues < 1) {
      store.dispatch(
        AnimationActions.crash(
          `Keno Snakes Multiplier | Unable to load animation for this number of results: ${numPossibleValues}`,
        ),
      )
    }

    if (!this.possibleValues.includes(this.multiplierValue)) {
      store.dispatch(
        AnimationActions.crash(
          `Keno Snakes Multiplier | Unable to load animation. Multiplier ${this.multiplierValue} is not a possible multiplier`,
        ),
      )
    }

    const gridSize = numPossibleValues > 9 ? 4 : 3
    const snakeSize = numPossibleValues > 9 ? 1.5 : 1.75

    this.grid = new Grid(gridSize, gridSize)

    this.circleContainer = this.scene.add.container(0, 0)

    this.background = this.scene.add.image(
      0,
      0,
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.BACKGROUND,
    )
    this.background.setOrigin(0.5, 0.5)

    this.foreground = this.scene.add.image(
      0,
      0,
      MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
      MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.FOREGROUND,
    )
    this.foreground.setOrigin(0.5, 0.5)

    this.snake = new KenoSnake(this.scene, 0, 0, snakeSize)
    this.snake.setVisible(false)

    this.add([this.background, this.foreground, this.circleContainer, this.snake])
  }

  loadTextures() {
    if (
      !this.scene.textures
        .getTextureKeys()
        .includes(MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.CIRCLE_BLUE_SMALL)
    ) {
      this.scene.textures.addSpriteSheetFromAtlas(
        MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.CIRCLE_BLUE_SMALL,
        {
          atlas: MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
          frame: MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.CIRCLE_BLUE_SMALL,
          frameWidth: 146,
          frameHeight: 146,
        },
      )
    }

    if (
      !this.scene.textures
        .getTextureKeys()
        .includes(MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.CIRCLE_BLUE_LARGE)
    ) {
      this.scene.textures.addSpriteSheetFromAtlas(
        MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.CIRCLE_BLUE_LARGE,
        {
          atlas: MULTIPLIER_KENO_SNAKES_TEXTURE_KEYS.ATLAS_NAME,
          frame: MULTIPLIER_KENO_SNAKES_ATLAS_KEYS.CIRCLE_BLUE_LARGE,
          frameWidth: 262,
          frameHeight: 262,
        },
      )
    }
  }

  setUpCircles() {
    let circlePosition = 0
    let index = 0

    for (let r = 0; r < this.grid.rows; r++) {
      for (let c = 0; c < this.grid.columns; c++) {
        const circle = new PossibleMultiplier(
          this.scene,
          CIRCLE_POSITIONS[this.grid.rows].xPositions[c],
          CIRCLE_POSITIONS[this.grid.rows].xPositions[r],
          { row: r, col: c },
          this.possibleValues[index],
          {
            noBonusSound: KENO_SNAKES_AUDIO_KEYS.NO_BONUS,
            bonusSound: KENO_SNAKES_AUDIO_KEYS.BONUS_CHOSEN,
            bonusEliminatedSound: KENO_SNAKES_AUDIO_KEYS.BONUS_ELIMINATED
          }
        )

        if (!REMOVED_CIRCLES[this.possibleValues.length].includes(circlePosition)) {
          this.circleContainer.add(circle)

          if (this.multiplierValue === this.possibleValues[index]) {
            this.resultCircle = circle
          } else {
            this.remainingTargets.push(circle)
          }

          circle

          index++
        } else {
          circle.setActive(false)
          circle.setVisible(false)
        }
        this.allCircles.push(circle)
        this.add(circle)

        circlePosition++
      }
    }
  }

  async targetCircles() {
    this.snake.setToTop()

    /*
      Create a weighted array of all multipliers that will be removed, making it more likely that a high multiplier will be removed first. 
      This is to create a more accurate portrayal of scarcity for higher multipliers.
    */
    let weightedTargets = this.remainingTargets
      .sort((a, b) => a.value - b.value)
      .flatMap((element, index) => Array(index + 8).fill(element))

    while (weightedTargets.length > 0) {
      const nextTarget = Utils.getRandomElementFromArray(weightedTargets)

      weightedTargets = weightedTargets.filter((target) => target.value !== nextTarget.value)

      this.remainingTargets = this.remainingTargets.filter(
        (target) => target.value !== nextTarget.value,
      )

      await tweenWaitPromise(this.scene, 100)()

      this.snakeSpeedMultiplier = Math.min(2, 1 + this.remainingTargets.length / 30)

      await this.snakeToCircle(nextTarget)
    }

    await tweenWaitPromise(this.scene, 200)()
    await this.resultCircle.showAsWinner()
  }

  async snakeToCircle(circle: PossibleMultiplier) {
    const startTop = this.grid.getRandomGridLocation(0, undefined)
    const startBottom = this.grid.getRandomGridLocation(this.grid.rows - 1, undefined)
    const startLeft = this.grid.getRandomGridLocation(undefined, 0)
    const startRight = this.grid.getRandomGridLocation(undefined, this.grid.columns - 1)

    const startingLoc = Utils.getRandomElementFromArray([
      startTop,
      startBottom,
      startLeft,
      startRight,
    ])

    const startPos = this.allCircles[this.grid.mapGridLocationToIndex(startingLoc)]
    let adjustedStartPos = {
      x: 0,
      y: 0,
    }
    if (startingLoc === startTop) {
      adjustedStartPos = {
        x: startPos.x,
        y: startPos.y + CIRCLE_POSITIONS[this.grid.rows].yPositions[0],
      }
    } else if (startingLoc === startBottom) {
      adjustedStartPos = {
        x: startPos.x,
        y: startPos.y - CIRCLE_POSITIONS[this.grid.rows].yPositions[0],
      }
    } else if (startingLoc === startLeft) {
      adjustedStartPos = {
        x: startPos.x + CIRCLE_POSITIONS[this.grid.rows].xPositions[0],
        y: startPos.y,
      }
    } else if (startingLoc === startRight) {
      adjustedStartPos = {
        x: startPos.x - CIRCLE_POSITIONS[this.grid.rows].xPositions[0],
        y: startPos.y,
      }
    }

    const enterFrom = Utils.getRandomElementFromArray(
      this.grid.adjacentGridLocation(circle.location),
    )

    const path = KenoSnakesUtils.createPath(this.grid, startingLoc, circle.location, enterFrom, 5)

    this.snake.setSnakePosition(adjustedStartPos.x, adjustedStartPos.y)
    this.snake.startMovement()

    for (let i = 0; i < path.length; i++) {
      const target = this.allCircles[this.grid.mapGridLocationToIndex(path[i].location)]

      await this.snake.moveSnake(target.x, target.y, 150 / this.snakeSpeedMultiplier)
    }

    await this.snake.stopMovement()
    await circle.disappear()
  }

  async play() {
    this.setUpCircles()
    await this.targetCircles()
    await tweenWaitPromise(this.scene, 1000)()
    await tweenPromise(this.scene, {
      targets: this,
      alpha: 0,
      duration: 1000,
    })()
    this.scene.tweens.killAll()
    this.destroy()
    return
  }

  destroy(fromScene?: boolean): void {
    this.allCircles.forEach((circle) => circle.removeAll(true))
    this.circleContainer.removeAll(true)
    super.destroy(fromScene)
  }
}
